import { LitElement, html } from 'lit';
import { customElement, property, query } from 'lit/decorators.js';
import { classMap } from 'lit/directives/class-map.js';
import '../icon/icon.element';
import { checkboxStyles } from './checkbox.styles';

let nextUniqueId = 0;

@customElement('cds-checkbox')
export class CheckboxElement extends LitElement {
  static styles = [checkboxStyles];

  @property({ type: String, attribute: 'aria-label' })
  ariaLabel = '';

  @property({ type: String, attribute: 'aria-labelledby' })
  ariaLabelledby = '';

  @property({ type: Boolean, reflect: true })
  checked = false;

  @property({ type: Boolean, reflect: true })
  disabled = false;

  @property({ type: String })
  id = `cds-checkbox-${++nextUniqueId}`;

  @property({ type: Boolean })
  indeterminate = false;

  @property({ type: String })
  name = '';

  @property({ type: Boolean })
  readonly = false;

  @property({ type: Boolean })
  required = false;

  @property({ type: Number })
  tabIndex = 0;

  @property({ type: String })
  value = '';

  @query('input')
  input!: HTMLInputElement;

  private onChange = (value: boolean) => {};
  // Angular: private onTouched = () => {};

  render() {
    const classes = {
      'checkbox--disabled': this.disabled,
      'checkbox--readonly': this.readonly,
      'checkbox--checked': this.checked,
      'checkbox--indeterminate': this.indeterminate
    };

    return html`
      <label class="cds-checkbox__checkbox ${classMap(classes)}" for=${this.inputId}>
        <span class="cds-checkbox__container">
          <span class="cds-checkbox__square">
            ${this.renderIcon()}
          </span>
          <input
            type="checkbox"
            class="cds-visually-hidden"
            .id=${this.inputId}
            .checked=${this.checked}
            ?disabled=${this.disabled || this.readonly}
            .tabIndex=${this.tabIndex}
            .name=${this.name}
            .value=${this.value}
            ?required=${this.required}
            aria-label=${this.ariaLabel || ''}
            aria-labelledby=${this.ariaLabelledby || ''}
            aria-checked=${this.ariaChecked}
            @change=${this.onInputChange}
            @click=${this.onInputClick}
          >
        </span>
        <span class="cds-checkbox__label-content">
          <slot></slot>
        </span>
      </label>
    `;
  }

  private renderIcon() {
    if (this.indeterminate) {
      return html`<cds-icon size="xs" icon="subtract" .notHoverable=${true}></cds-icon>`;
    }
    if (this.checked) {
      return html`<cds-icon size="xs" icon="checkmark" .notHoverable=${true}></cds-icon>`;
    }
    return '';
  }

  get inputId(): string {
    return `${this.id}-input`;
  }

  get ariaChecked(): 'true' | 'false' | 'mixed' {
    if (this.indeterminate) {
      return 'mixed';
    }
    return this.checked ? 'true' : 'false';
  }

  private onInputChange(event: Event) {
    event.stopPropagation();
  }

  private onInputClick(event: MouseEvent) {
    event.stopPropagation();

    if (this.disabled || this.readonly) {
      this.syncChecked();
      this.syncIndeterminate();
      return;
    }

    if (this.indeterminate) {
      this.indeterminate = false;
      this.dispatchEvent(new CustomEvent('indeterminate-change', {
        detail: this.indeterminate,
        bubbles: true,
        composed: true
      }));
    }

    this.checked = !this.checked;
    this.emitChange();
  }

  private syncChecked() {
    if (this.input) {
      this.input.checked = this.checked;
    }
  }

  private syncIndeterminate() {
    if (this.input) {
      this.input.indeterminate = this.indeterminate;
    }
  }

  private emitChange() {
    this.onChange(this.checked);
    // this.dispatchEvent(new CheckboxChangeEvent(this.checked, this));
    this.dispatchEvent(new CustomEvent('change', {
      detail: this.checked,
      bubbles: true,
      composed: true
    }));
  }

  // Form control API methods
  formAssociatedCallback() {
    // Add form-associated custom element behavior
  }

  formDisabledCallback(disabled: boolean) {
    this.disabled = disabled;
  }

  formStateRestoreCallback(state: boolean) {
    this.checked = state;
  }
}

declare global {
  interface HTMLElementTagNameMap {
    'cds-checkbox': CheckboxElement;
  }
}
